/**
 * WebSocket Manager for Real-time Dialogue
 * Handles WebSocket connection, message sending/receiving, and reconnection
 */

class WebSocketManager {
  constructor(lessonId, onMessage, onError, onClose, token = null) {
    this.lessonId = lessonId;
    this.ws = null;
    this.isConnected = false;
    this.messageQueue = [];
    this.token = token;
    
    // Callbacks
    this.onMessage = onMessage;
    this.onError = onError;
    this.onClose = onClose;
    
    // Base URL - using production URL from backend dev
    this.baseUrl = 'wss://app.ailingua.net';
  }

  /**
   * Get authentication token (JWT or null for session-based)
   */
  getAuthToken() {
    // Try to get JWT from localStorage or sessionStorage
    if (this.token) {
      return this.token;
    }
    
    // Try localStorage first
    const jwtToken = localStorage.getItem('jwt_token') || sessionStorage.getItem('jwt_token');
    if (jwtToken) {
      return jwtToken;
    }
    
    // Return null to use session cookies
    return null;
  }

  /**
   * Connect to WebSocket
   */
  connect() {
    return new Promise((resolve, reject) => {
      try {
        let url = `${this.baseUrl}/ws/dialog/${this.lessonId}/`;
        
        // Add token to query if available (JWT auth)
        const token = this.getAuthToken();
        if (token) {
          url += `?token=${encodeURIComponent(token)}`;
        }
        
        console.log('🔌 Connecting to WebSocket:', url);
        
        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
          console.log('✅ WebSocket connected!');
          this.isConnected = true;
          
          // Send any queued messages
          this.flushMessageQueue();
          
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('📨 Received:', data.type, data);
            
            if (this.onMessage) {
              this.onMessage(data);
            }
          } catch (error) {
            console.error('Error parsing message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('❌ WebSocket error:', error);
          
          if (this.onError) {
            this.onError(error);
          }
          
          reject(error);
        };

        this.ws.onclose = (event) => {
          console.log('🔌 WebSocket closed:', event.code, event.reason);
          this.isConnected = false;
          
          if (this.onClose) {
            this.onClose(event);
          }
        };

      } catch (error) {
        console.error('Error creating WebSocket:', error);
        reject(error);
      }
    });
  }

  /**
   * Send message to WebSocket
   */
  send(message) {
    if (this.isConnected && this.ws.readyState === WebSocket.OPEN) {
      console.log('📤 Sending:', message.type);
      this.ws.send(JSON.stringify(message));
    } else {
      console.log('⏳ Queueing message (not connected yet)');
      this.messageQueue.push(message);
    }
  }

  /**
   * Flush queued messages
   */
  flushMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.send(message);
    }
  }

  /**
   * Start dialogue session
   */
  startSession(mode = 'manual', roleMode = 'default', useAIValidation = true) {
    this.send({
      type: 'control.start',
      mode: mode,
      role_mode: roleMode,
      use_ai_validation: useAIValidation
    });
  }

  /**
   * Send audio chunk
   */
  sendAudioChunk(base64Audio, format = 'webm', chunkIndex = 0, totalChunks = 1, isLast = true) {
    this.send({
      type: 'audio.chunk',
      data: base64Audio,
      format: format,
      sample_rate: 48000,
      chunk_index: chunkIndex,
      total_chunks: totalChunks,
      is_last: isLast
    });
  }

  /**
   * Stop recording (manual mode)
   */
  stopRecording() {
    this.send({
      type: 'control.stop_recording'
    });
  }

  /**
   * Request hint
   */
  requestHint() {
    this.send({
      type: 'control.hint_request'
    });
  }

  /**
   * Skip correction repetition
   */
  skip() {
    this.send({
      type: 'control.skip'
    });
  }

  /**
   * Request question clarification (translation)
   */
  clarifyQuestion() {
    this.send({
      type: 'control.question_clarify'
    });
  }

  /**
   * Request translation
   * @param {string} text - Optional text to translate. If not provided, translates last AI message
   */
  requestTranslation(text = null) {
    const message = {
      type: 'control.translate'
    };
    
    if (text) {
      message.text = text;
    }
    
    this.send(message);
  }

  /**
   * End session
   */
  endSession() {
    this.send({
      type: 'control.end'
    });
  }

  /**
   * Close WebSocket connection
   */
  close() {
    if (this.ws) {
      this.ws.close();
      this.isConnected = false;
    }
  }

  /**
   * Check if connected
   */
  isReady() {
    return this.isConnected && this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

export default WebSocketManager;
